import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { ArrowUpRight, Contact, Download, Mail, Printer, X } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { money } from "../lib/finance";
import { API } from "../lib/apiClient";
import { Modal } from "./common";
import { BusinessCardModal } from "./BusinessCardModal";

export function ConvertQuoteModal({ quote, onSubmit, onClose }) {
  const today = new Date().toISOString().slice(0, 10);
  const nextYear = new Date(); nextYear.setFullYear(nextYear.getFullYear() + 1);
  const [policeNo, setPoliceNo] = useState("");
  const [start, setStart] = useState(today);
  const [end, setEnd] = useState(nextYear.toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!policeNo.trim()) { setErr("Poliçe numarası zorunludur."); return; }
    if (!start || !end) { setErr("Başlangıç ve bitiş tarihi gerekli."); return; }
    if (new Date(end) <= new Date(start)) { setErr("Bitiş tarihi başlangıçtan sonra olmalı."); return; }
    setBusy(true); setErr("");
    const res = await onSubmit({ policeNo: policeNo.trim(), start, end });
    setBusy(false);
    if (!res?.ok) setErr(res?.error || "Dönüştürme başarısız.");
  };

  return (
    <Modal title="Teklifi Poliçeye Dönüştür" onClose={busy ? () => {} : onClose}>
      <form onSubmit={submit} className="modal-body" data-testid="convert-quote-form">
        <div className="convert-summary panel" style={{ background: "#eff6ff", border: "1px solid #bfdbfe", padding: 14, borderRadius: 8, marginBottom: 16 }}>
          <div className="small muted" style={{ marginBottom: 4 }}>Dönüştürülecek Teklif</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, fontSize: 12 }}>
            <div><strong>Müşteri:</strong> {quote.customerName || "—"}</div>
            <div><strong>Branş:</strong> {quote.branch || "—"}</div>
            <div><strong>Şirket:</strong> {quote.company || "—"}</div>
            <div><strong>Plaka:</strong> {quote.plate || "—"}</div>
            {quote.tcKimlik && <div><strong>TC:</strong> {quote.tcKimlik}</div>}
            {quote.ruhsatSeriNo && <div><strong>Ruhsat:</strong> {quote.ruhsatSeriNo}</div>}
            <div><strong>Prim:</strong> {quote.premium ? money(quote.premium) : "—"}</div>
            <div><strong>Komisyon:</strong> %{quote.commissionRate}</div>
          </div>
        </div>
        <label className="field">
          <span>Poliçe Numarası *</span>
          <input data-testid="convert-police-no" value={policeNo} onChange={(e) => setPoliceNo(e.target.value)} placeholder="Örn: 1234567890" autoFocus />
        </label>
        <div className="form-grid">
          <label className="field">
            <span>Başlangıç Tarihi *</span>
            <input data-testid="convert-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} />
          </label>
          <label className="field">
            <span>Bitiş Tarihi *</span>
            <input data-testid="convert-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
          </label>
        </div>
        <p className="muted small" style={{ marginTop: 10 }}>
          Teklifteki tüm bilgiler (müşteri, branş, prim, komisyon, not, plaka) yeni poliçeye aktarılacak. Teklif "Dönüştürüldü" olarak işaretlenecek.
        </p>
        {err && <div className="auth-error" data-testid="convert-error"><X size={14} /> {err}</div>}
        <div className="modal-actions">
          <button type="button" className="outline-button" data-testid="convert-cancel-button" onClick={onClose} disabled={busy}>Vazgeç</button>
          <button type="submit" className="primary-button" data-testid="convert-confirm-button" disabled={busy}>
            {busy ? "Dönüştürülüyor…" : <>Poliçeye Dönüştür <ArrowUpRight size={14} /></>}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function OnlineQuoteLinkModal({ slug, onFetch, onRegenerate, onClose }) {
  const [copied, setCopied] = useState(false);
  const [card, setCard] = useState(false);
  const qrRef = useRef(null);
  useEffect(() => { if (!slug) onFetch(); }, [slug, onFetch]);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const fullUrl = slug ? `${origin}/q/${slug}` : "";
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { window.prompt("Linki kopyalayın:", fullUrl); }
  };
  const downloadQr = () => {
    const svgEl = qrRef.current?.querySelector("svg");
    if (!svgEl) return;
    const serializer = new XMLSerializer();
    let svgStr = serializer.serializeToString(svgEl);
    if (!svgStr.includes("xmlns=")) {
      svgStr = svgStr.replace("<svg", '<svg xmlns="http://www.w3.org/2000/svg"');
    }
    // Render SVG to PNG 512x512 for high-res print on cards/signs
    const img = new Image();
    const svg64 = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgStr)));
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 512; canvas.height = 512;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, 512, 512);
      ctx.drawImage(img, 0, 0, 512, 512);
      canvas.toBlob((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `teklif-qr-${slug || "code"}.png`;
        document.body.appendChild(a); a.click(); a.remove();
      }, "image/png");
    };
    img.src = svg64;
  };
  const printQr = () => {
    const win = window.open("", "_blank");
    if (!win) return;
    const html = `<!doctype html><html><head><title>Teklif QR · ${slug}</title>
      <style>
        body{font-family:system-ui,-apple-system,Segoe UI,sans-serif;display:grid;place-items:center;min-height:100vh;margin:0;padding:24px;color:#0f172a}
        .card{text-align:center;max-width:420px}
        .card h1{font-size:22px;margin:0 0 6px}
        .card p{color:#64748b;font-size:14px;margin:0 0 24px}
        .qr-box{display:inline-block;padding:16px;border:1px solid #e2e8f0;border-radius:14px;background:#fff;box-shadow:0 10px 30px #0f172a14}
        .url{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:13px;color:#1a5fe0;margin-top:16px;word-break:break-all}
        @media print{.card{page-break-inside:avoid}}
      </style></head><body>
      <div class="card">
        <h1>Online Teklif Al</h1>
        <p>Telefonunuzun kamerasıyla kodu tarayın.</p>
        <div class="qr-box">${qrRef.current?.innerHTML || ""}</div>
        <div class="url">${fullUrl}</div>
      </div>
      <script>window.onload=()=>setTimeout(()=>window.print(),300)</script>
      </body></html>`;
    win.document.open(); win.document.write(html); win.document.close();
  };
  if (card) return <BusinessCardModal qrUrl={`${fullUrl}?src=qr`} onBack={() => setCard(false)} onClose={onClose} />;
  return (
    <Modal title="Online Teklif Linkim" onClose={onClose}>
      <div className="modal-body" data-testid="online-link-modal">
        <p className="muted">Bu linki Instagram biyografinize, WhatsApp durumunuza veya kartvizitinize ekleyin. Linki açan kişiler hesap oluşturmadan size teklif talebi gönderebilir.</p>
        {slug && (
          <div style={{ display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap", padding: "18px 0" }}>
            <div ref={qrRef} className="qr-container" data-testid="online-link-qr" style={{ padding: 12, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, lineHeight: 0 }}>
              <QRCodeSVG value={`${fullUrl}?src=qr`} size={160} level="M" bgColor="#ffffff" fgColor="#0f172a" includeMargin={false} />
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <strong style={{ fontSize: 13, display: "block", marginBottom: 6 }}>Kartvizit / Tabela için QR</strong>
              <p className="muted small" style={{ margin: "0 0 12px" }}>PNG olarak indirin veya doğrudan yazdırın. 512×512 çözünürlükte baskıya hazır.</p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button type="button" className="outline-button" data-testid="online-link-qr-download" onClick={downloadQr}><Download size={14} /> PNG indir</button>
                <button type="button" className="outline-button" data-testid="online-link-qr-print" onClick={printQr}><Printer size={14} /> Yazdır</button>
                <button type="button" className="primary-button" data-testid="online-link-business-card" onClick={() => setCard(true)}><Contact size={14} /> QR kartvizit</button>
              </div>
            </div>
          </div>
        )}
        <div className="field">
          <span>Paylaşılabilir Link</span>
          <div style={{ display: "flex", gap: 8 }}>
            <input data-testid="online-link-url" readOnly value={fullUrl} style={{ flex: 1 }} />
            <button className="primary-button" data-testid="online-link-copy" onClick={copy} disabled={!slug}>{copied ? "Kopyalandı ✓" : "Kopyala"}</button>
          </div>
        </div>
        <div className="modal-actions" style={{ justifyContent: "space-between" }}>
          <button className="outline-button danger" data-testid="online-link-regen" onClick={onRegenerate}>Linki yenile (eskisi iptal)</button>
          <button className="primary-button" data-testid="online-link-close" onClick={onClose}>Tamam</button>
        </div>
      </div>
    </Modal>
  );
}

export function QuoteEmailModal({ quote, onClose }) {
  const [to, setTo] = useState(quote.customerEmail || "");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    if (!to.trim()) { setStatus({ ok: false, text: "E-posta adresi zorunludur." }); return; }
    setBusy(true); setStatus(null);
    try {
      const { data } = await axios.post(`${API}/quotes/${quote.id}/send-email`, { to: to.trim(), message: message.trim() });
      setStatus({ ok: true, text: `✓ E-posta gönderildi (${to}).${data?.pdfUrl ? " PDF linki de iletildi." : ""}` });
    } catch (e2) {
      setStatus({ ok: false, text: e2?.response?.data?.detail || "Gönderim başarısız." });
    } finally { setBusy(false); }
  };
  return (
    <Modal title="Teklifi E-posta ile Gönder" onClose={busy ? () => {} : onClose}>
      <form onSubmit={submit} className="modal-body" data-testid="quote-email-form">
        <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", padding: 12, borderRadius: 8, marginBottom: 14 }}>
          <div className="small"><strong>Müşteri:</strong> {quote.customerName || "—"} · <strong>Branş:</strong> {quote.branch || "—"} · <strong>Prim:</strong> {quote.premium ? money(quote.premium) : "—"}</div>
          {!!(quote.pdfs && quote.pdfs.length) && <div className="small muted" style={{ marginTop: 4 }}>📎 PDF teklif eki içerir — e-postada güvenli link paylaşılır.</div>}
        </div>
        <label className="field">
          <span>Alıcı E-posta *</span>
          <input data-testid="quote-email-to" type="email" value={to} onChange={(e) => setTo(e.target.value)} required autoFocus />
        </label>
        <label className="field">
          <span>Kişisel Mesaj (opsiyonel)</span>
          <textarea data-testid="quote-email-message" rows="3" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Örn: Merhaba, görüşmemize istinaden hazırladığım teklif ektedir."></textarea>
        </label>
        {status && <div className="auth-error" data-testid="quote-email-status" style={{ background: status.ok ? "#dcfce7" : "#fee2e2", color: status.ok ? "#166534" : "#b91c1c", borderColor: status.ok ? "#bbf7d0" : "#fecaca" }}>{status.text}</div>}
        <div className="modal-actions">
          <button type="button" className="outline-button" data-testid="quote-email-cancel" onClick={onClose} disabled={busy}>Vazgeç</button>
          <button type="submit" className="primary-button" data-testid="quote-email-send" disabled={busy}>{busy ? "Gönderiliyor..." : (<><Mail size={14} /> Gönder</>)}</button>
        </div>
      </form>
    </Modal>
  );
}
